import React, { ReactElement, useRef, useState, useEffect } from "react";
import {AssetURL, DpasFileType, IsImage} from "../../constants";
import { downloadFile, deleteFile } from "../../BackendFacade/apiCalls";
import { useMain } from "../../../../Utils/SessionControls/mainContext";
import { DpasPopup, IFileDetails } from "../../Interfaces/IFiles";
import { createDownloadObject } from "../../../../Utils/objectDownload";
import { odxIconSets } from "../../../../OdxComponents/constants";
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxDropdown from "../../../../OdxComponents/NativeOdx/Odx-Dropdown";
import {simplifyForHtmlId} from "../../../../Utils/transformer";

export interface IFilesDetail {
    refreshDirectory: () => void;
    fileDetails: IFileDetails;
    directoryType: DpasFileType;
    projectId: string;
}

const FilesDetail: React.FC<IFilesDetail> = ({
                                                 refreshDirectory,
                                                 fileDetails,
                                                 directoryType,
                                                 projectId,
                                             }) => {
    const { setLoading, createToast, createErrorToast } = useMain();
    const [isDownloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
    const [isVersionDropdownOpen, setVersionDropdownOpen] = useState(false);
    const [isCopyDropdownOpen, setCopyDropdownOpen] = useState(false);
    const baseId = `dpas-files-detail-${simplifyForHtmlId(fileDetails?.fileName ?? 'file')}`;

    const downloadButtonRef = useRef<HTMLDivElement>(null);
    const copyButtonRef = useRef<HTMLDivElement>(null);
    const dropdownContainerRef = useRef<HTMLDivElement>(null);
    const modalContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleOutsideClick(event: MouseEvent) {
            if (
                (modalContainerRef.current &&
                    modalContainerRef.current.contains(event.target as Node)) ||
                (dropdownContainerRef.current &&
                    dropdownContainerRef.current.contains(event.target as Node))
            ) {
                return;
            }
            dpasPopupClose();
        }
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const dpasPopupClose = () => {
        // todo: pls fix, thanks
        setPopup(DpasPopup.None);
    };

    const handleDownloadFile = (path: string, resolution: string = "", versionId = "") => {
        downloadFile(path, directoryType, resolution, versionId).then((data) => {
            if (!data.success) {
                createErrorToast(data.message);
            } else {
                createDownloadObject(data.data, path.split("/").pop());
            }
            createToast({ message: "Download started", variant: "default", title: "Download" });
        });
    };

    // New handler for deletion using browser confirm
    const handleDeleteFile = (path: string, directoryType: string) => {
        if (window.confirm(`Are you sure you want to delete ${path}?`)) {
            setLoading(true);
            deleteFile(path, directoryType).then((data) => {
                if (!data.success) {
                    createErrorToast(data.message);
                } else {
                    refreshDirectory();
                    dpasPopupClose();
                }
                setLoading(false);
            });
        }
    };

    let Infos: ReactElement;
    let deleteButton = (
        <odx-button
            key="delete"
            id={`OdxButton_${baseId}-delete-btn`}
            variant="danger"
            size="lg"
            type="button"
            className="mr-[10px]"
            onClick={() => {
                handleDeleteFile(fileDetails.fileName, directoryType);
            }}
        >
            Delete
        </odx-button>
    );

    if (IsImage(directoryType)) {
        Infos = (
            <>
                <div className={"mb-[15px]"}>
                    <div className="odx-grid-col--3">Picture</div>
                    <div>
                        <img
                            className={"max-h-[calc(100vh-775px)] w-full block rounded-tl-[1rem] rounded-tr-[1rem] aspect-square object-contain"}
                            src={AssetURL + "1080px/" + projectId + "/" + fileDetails.fileName}
                            alt={"picture"}
                        />
                    </div>
                </div>
                <div className={"mb-[15px]"}>
                    <div className="odx-grid-col--3">Available Resolutions</div>
                    <div>{fileDetails.availableResolutions.join(", ")}</div>
                </div>
            </>
        );
    } else {
        Infos = (
            <div className={"mb-[15px]"}>
                <div className="odx-grid-col--3">File Size</div>
                <div>{Math.round((fileDetails.length / 1024 / 1024) * 100) / 100} MB</div>
            </div>
        );
    }


    return (
        <>
            <div ref={dropdownContainerRef}>
                {IsImage(directoryType) && (
                    <>
                        <OdxDropdown
                            content={fileDetails.availableResolutions.map((res, index) => (
                                <>
                                    <div
                                        key={index}
                                        id={`${baseId}-download-option-${simplifyForHtmlId(res)}`}
                                        onClick={() => handleDownloadFile(fileDetails.fileName, res)}
                                        className={`odx-select-option odx-option "is-active"`}
                                    >
                                        {res}
                                    </div>
                                    {index < fileDetails.availableResolutions.length - 1 && (
                                        <odx-separator style={{margin: '1rem 0'}} />
                                    )}
                                </>
                            ))}
                            position="top-start"
                            showArrow={false}
                            matchReferenceWidth={false}
                            offset={0}
                            isOpen={isDownloadDropdownOpen}
                            setIsOpen={setDownloadDropdownOpen}
                            buttonRef={downloadButtonRef}
                        />

                        <OdxDropdown
                            content={fileDetails.availableResolutions.map((res, index) => (
                                <>
                                    <div
                                        key={index}
                                        id={`${baseId}-copy-option-${simplifyForHtmlId(res)}`}
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                AssetURL +
                                                res +
                                                "/" +
                                                projectId +
                                                "/" +
                                                fileDetails.fileName.replace(/^px/, "")
                                            )
                                            createToast({
                                                message: "Link copied to clipboard!",
                                                variant: "default",
                                                title: "Link copied"
                                            });
                                        }}
                                        className={`odx-select-option odx-option "is-active"`}
                                    >
                                        {res}
                                    </div>
                                    {index < fileDetails.availableResolutions.length - 1 && (
                                        <odx-separator style={{margin: '1rem 0'}} />
                                    )}
                                </>
                            ))}
                            position="top-start"
                            showArrow={false}
                            matchReferenceWidth={false}
                            offset={0}
                            isOpen={isCopyDropdownOpen}
                            setIsOpen={setCopyDropdownOpen}
                            buttonRef={copyButtonRef}
                        />
                    </>
                )}

                {directoryType === "document" && (
                    <OdxDropdown
                        content={fileDetails.versions.map((vers, index) => (
                            <>
                                <div onClick={(e) =>
                                {e.stopPropagation()
                                    handleDownloadFile(fileDetails.fileName, "", vers.versionId)
                                }}
                                    key={666 + index}
                                    id={`${baseId}-version-option-${simplifyForHtmlId(vers.versionId || 'latest')}`}
                                    className={`odx-select-option odx-option "is-active"`}
                                >
                                    {index === 0 ? "Latest" : vers.versionId} {/* Display "Latest" for the first item */}
                                </div>
                                {index < fileDetails.versions.length - 1 && (
                                    <odx-separator style={{margin: '1rem 0'}} />
                                )}
                            </>
                        ))}
                        position="top-start"
                        showArrow={false}
                        matchReferenceWidth={false}
                        offset={0}
                        isOpen={isVersionDropdownOpen}
                        setIsOpen={setVersionDropdownOpen}
                        buttonRef={downloadButtonRef}
                    />
                )}

            </div>


            {/* Wrapper um das Modal, über den der Klick außerhalb erkannt wird */}
            <div ref={modalContainerRef}>
                <OdxModal
                    size="medium"
                    variant="default"
                    title="Details"
                    id={baseId}
                    isOpen={true}
                    onClose={dpasPopupClose}
                    footerButtons={[
                        deleteButton,
                        directoryType !== "Documents" &&
                        directoryType !== "Secrets" && (
                            <div key="downloadButtonRef" ref={downloadButtonRef}>
                                <odx-button
                                    id={`OdxButton_${baseId}-download-btn`}
                                    variant="primary"
                                    size="lg"
                                    type="button"
                                    className="mr-[15px]"
                                    onClick={() => {
                                        if (IsImage(directoryType)) {
                                            setDownloadDropdownOpen(true);
                                        } else if (!IsImage(directoryType) && fileDetails.versions.length > 1) {
                                            setVersionDropdownOpen(true)
                                        } else {
                                            handleDownloadFile(fileDetails.fileName);
                                        }
                                    }}
                                >
                                    <odx-icon slot="prefix" name={`${odxIconSets.core}::download`} size="md"></odx-icon>
                                    Download
                                </odx-button>
                            </div>
                        ),
                        <div key="copy" ref={copyButtonRef}>
                            <odx-button
                                id={`OdxButton_${baseId}-copy-btn`}
                                variant="primary"
                                size="lg"
                                type="button"
                                onClick={() => {
                                    if (IsImage(directoryType)) {
                                        setCopyDropdownOpen(true);
                                    } else {
                                        navigator.clipboard.writeText(
                                            AssetURL + "documents" + "/" + projectId + "/" + fileDetails.fileName
                                        );
                                        createToast({
                                            message: "Link copied to clipboard!",
                                            variant: "default",
                                            title: "Link copied"
                                        });
                                    }
                                }}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::copy-content`} size="md"></odx-icon>
                                Copy Link
                            </odx-button>
                        </div>,
                    ]}
                >
                    <div>
                        <div className={"mb-[15px]"}>
                            <div className="odx-grid-col--3">File Name</div>
                            <div>{fileDetails.fileName?.toString().split("/").pop()}</div>
                        </div>
                        {Infos}
                        <div className={"mb-[15px]"}>
                            <div className="odx-grid-col--3">Created</div>
                            <div>{fileDetails.properties.created}</div>
                        </div>
                        <div className={"mb-[15px]"}>
                            <div className="odx-grid-col--3">Last Modified</div>
                            <div>{fileDetails.properties.lastModified}</div>
                        </div>
                    </div>
                </OdxModal>
            </div>
        </>
    );
};

export default FilesDetail;
